import Sidebar from "../components/Sidebar";
import { useUser } from "../contexts/UserContext";
import useMoveTo from "../constants/navigation";
import MobileHeader from "../components/MobileHeader";
import { ICONS } from "../constants/icons";
import { useEffect, useState } from "react";
import { ref, onValue, update, get } from "firebase/database";
import { database } from "../constants/firebase";
import { ROUTES } from "../App";


const Activity = () => {
    const navigate = useMoveTo();
    const { currentUser } = useUser();
    const [requests, setRequests] = useState([]);
    const [loadingRequests, setLoadingRequests] = useState(true);


    useEffect(() => {
        if (!currentUser) return;

        const requestsRef = ref(database, `Users/${currentUser.uid}/friendRequests`);

        const unsubscribe = onValue(requestsRef, async (snapshot) => {
            if (!snapshot.exists()) {
                setRequests([]);
                setLoadingRequests(false);
                return;
            }

            const data = snapshot.val();
            const receivedIds = Object.keys(data).filter(
                (uid) => data[uid] === "received"
            );

            const usersData = await Promise.all(
                receivedIds.map(async (uid) => {
                    const snap = await get(ref(database, `Users/${uid}`));
                    return snap.exists() ? { id: uid, ...snap.val() } : null;
                })
            );
            setRequests(usersData.filter(Boolean));

            setLoadingRequests(false);
        });

        return () => unsubscribe();
    }, [currentUser]);


    const handleAccept = async (senderId) => {
        // Optimistically remove from UI
        setRequests((prev) => prev.filter((u) => u.id !== senderId));

        const updates = {};
        updates[`Users/${currentUser.uid}/friends/${senderId}`] = {
            createdAt: new Date().toISOString()
        };
        updates[`Users/${senderId}/friends/${currentUser.uid}`] = {
            createdAt: new Date().toISOString()
        };
        updates[`Users/${currentUser.uid}/friendRequests/${senderId}`] = null;
        updates[`Users/${senderId}/friendRequests/${currentUser.uid}`] = null;

        try {
            await update(ref(database), updates);
        } catch (err) {
            console.error(err);
            // If error, optionally revert UI
        }
    };

    const handleCancel = async (senderId) => {
        setRequests((prev) => prev.filter((u) => u.id !== senderId));

        const updates = {};
        updates[`Users/${currentUser.uid}/friendRequests/${senderId}`] = null;
        updates[`Users/${senderId}/friendRequests/${currentUser.uid}`] = null;

        try {
            await update(ref(database), updates);
        } catch (err) {
            console.error(err);
        }
    };


    const handleGoBack = () => {
        // Try to go back, if there's no history, go to Chats
        if (window.history.length > 2) { // >2 because typically there's at least the current page and the previous
            navigate(-1);
        } else {
            navigate(ROUTES.PROFILE);
        }
    };


    // 🟢 Simple shimmer skeleton loader component
    const ShimmerLoader = () => (
        <div className="flex flex-col space-y-4 mt-8">
            {[...Array(6)].map((_, i) => (
                <div
                    key={i}
                    className="animate-pulse flex items-center justify-between p-4 border-t border-gray-200 dark:border-[#D9D9D9]/20"
                >
                    <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 bg-gray-300 dark:bg-gray-700 rounded-lg"></div>
                        <div>
                            <div className="w-20 h-4 bg-gray-300 dark:bg-gray-700 rounded mb-2"></div>
                            <div className="w-12 h-4 bg-gray-300 dark:bg-gray-700 rounded mb-2"></div>
                        </div>
                    </div>
                    <div className="w-5 h-6 bg-gray-300 dark:bg-gray-700 rounded"></div>
                </div>
            ))}
        </div>
    );


    return (
        <div className="h-screen flex flex-col bg-white dark:bg-[#1A1A1A] text-black dark:text-white">
            <MobileHeader title={"Activity"} isBackBtn={true} handleBack={handleGoBack} />
            <div className="flex flex-1 overflow-hidden">
                <Sidebar />
                <main className="flex-1 min-h-0 overflow-y-auto pt-5 pb-24 md:pb-20 px-6 bg-white text-black dark:bg-[#1A1A1A] dark:text-white">
                    <div className="flex items-center justify-between pb-4">
                        <button onClick={handleGoBack} className="flex-shrink-0 md:block hidden">
                            <img src={ICONS.arrowBack} className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert" alt="Back" />
                        </button>
                        <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">Activity</h2>
                        <div />
                    </div>


                    {/* Activity */}
                    <div className="p-3 rounded-2xl mt-4 bg-[#F5F9FE] dark:bg-[#1f1f1f] border border-gray-200 dark:border-gray-700">
                        {loadingRequests ? (
                            <ShimmerLoader />
                        ) : requests.length === 0 ? (
                            <p className="text-center text-white py-6">
                                No friend requests
                            </p>
                        ) : (
                            <div className="space-y-4">
                                {requests.map((user, index) => (
                                    <div
                                        key={user.id}
                                        className={`flex items-center justify-between p-4 border-b ${index === requests.length - 1 ? "border-b-0" : "border-gray-200 dark:border-gray-700"}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <img
                                                src={
                                                    user.profilePic ||
                                                    "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef"
                                                }
                                                className="w-12 h-12 rounded-full object-cover"
                                                alt={user.name}
                                            />
                                            <div>
                                                <p className="font-semibold">{user.name}</p>
                                                <p className="text-sm text-primary">{user.niche || "No niche"}</p>
                                            </div>
                                        </div>

                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleAccept(user.id)}
                                                className="px-3 py-1 text-xs bg-primary text-white rounded-lg"
                                            >
                                                Accept
                                            </button>
                                            <button
                                                onClick={() => handleCancel(user.id)}
                                                className="px-3 py-1 text-xs bg-red-500 text-white rounded-lg"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                        )}

                    </div>

                </main>
            </div>
        </div>
    );
};

export default Activity;
